import { ChangeEvent, MouseEvent, ReactNode } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { CreditDebitNoteOriginalBill } from "../../types/credit-debit-note.type";
import { TransactionDetailWithItems, TransactionListRow } from "../../types/sale.type";
import { formatNumber } from "../../utils";
import { formatOrderDate } from "../SpecialOrder/data";
import BuyerSection from "./BuyerSection";
import InvoiceSearch from "./InvoiceSearch";
import {
  MANUAL_TAX_TYPE_OPTIONS,
  NOTE_SETTLEMENT_LABEL,
  NOTE_SETTLEMENT_OPTIONS,
  taxTypeLabel,
} from "./constants";
import { InvoiceSource, NoteDraft, NoteFormErrors, sanitizeMoney } from "./noteDraft";

interface OriginalInvoiceSectionProps {
  draft: NoteDraft;
  errors: NoteFormErrors;
  /** The original invoice's ex-VAT value the totals use (see draftOriginalValue). */
  originalValue: number | null;
  /** The picked branch, for head office; undefined for a branch user. */
  branchUuid?: string;
  companyId?: string;
  isBranchMissing: boolean;
  loadingInvoice: boolean;
  /** Why the picked bill cannot carry a note (the server's refusal); "" when it can. */
  billLoadError: string;
  onChange: (patch: Partial<NoteDraft>) => void;
  onChangeSource: (source: InvoiceSource) => void;
  onSelectInvoice: (row: TransactionListRow | null) => void;
}

const INVOICE_SOURCES: { value: InvoiceSource; label: string }[] = [
  { value: "LINKED", label: "อ้างอิงบิลในระบบ" },
  { value: "MANUAL", label: "กรอกเอง" },
];

function PreviewField({ label, value }: Readonly<{ label: string; value: ReactNode }>) {
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value || "-"}
      </Typography>
    </Stack>
  );
}

/** What the linked bill hands the note: buyer, route, tax type, original value and settlement. */
function LinkedInvoicePreview({
  invoice,
  originalBill,
  originalValue,
}: Readonly<{
  invoice: TransactionDetailWithItems;
  originalBill: CreditDebitNoteOriginalBill;
  originalValue: number | null;
}>) {
  const customerLabel = [invoice.customer_code, invoice.customer_name].filter(Boolean).join(" ");
  return (
    <Stack
      direction="row"
      gap={3}
      flexWrap="wrap"
      p={2}
      borderRadius="10px"
      bgcolor="background.default"
    >
      <PreviewField
        label="วันที่ใบกำกับภาษีเดิม"
        value={formatOrderDate(invoice.transaction_date)}
      />
      <PreviewField label="ลูกค้า" value={customerLabel} />
      <PreviewField label="เลขประจำตัวผู้เสียภาษี" value={invoice.inv_taxid} />
      <PreviewField label="สายส่ง" value={invoice.route_code} />
      <PreviewField label="ราคา" value={taxTypeLabel(invoice.tax_type)} />
      <PreviewField label="มูลค่าก่อน VAT" value={formatNumber(originalValue)} />
      <PreviewField label="ยอดสุทธิ" value={formatNumber(invoice.grand_total)} />
      <PreviewField
        label="การชำระเงิน (ตามบิลเดิม)"
        value={NOTE_SETTLEMENT_LABEL[originalBill.settlement_method]}
      />
    </Stack>
  );
}

/** The invoice the note corrects: a bill found in the system, or one keyed in by hand. */
export default function OriginalInvoiceSection({
  draft,
  errors,
  originalValue,
  branchUuid,
  companyId,
  isBranchMissing,
  loadingInvoice,
  billLoadError,
  onChange,
  onChangeSource,
  onSelectInvoice,
}: Readonly<OriginalInvoiceSectionProps>) {
  const handleSourceChange = (_event: MouseEvent<HTMLElement>, value: InvoiceSource | null) => {
    if (value) onChangeSource(value);
  };
  const handleInvoiceNoChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ manualInvoiceNo: event.target.value });
  const handleInvoiceDateChange = (value: Dayjs | null) => onChange({ manualInvoiceDate: value });
  const handleOriginalValueChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ manualOriginalValue: sanitizeMoney(event.target.value) });
  const handleTaxTypeChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({
      manualTaxType:
        MANUAL_TAX_TYPE_OPTIONS.find((option) => option.value === event.target.value)?.value ?? "",
    });
  const handleSettlementChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({
      settlementMethod:
        NOTE_SETTLEMENT_OPTIONS.find((option) => option === event.target.value) ?? "",
    });

  const branchHelperText = isBranchMissing ? "เลือกสาขาก่อน" : undefined;
  // The bill and its lines land together; nothing shows while a newer pick is still being read.
  const linkedBill =
    !loadingInvoice && draft.invoice && draft.originalBill
      ? { invoice: draft.invoice, originalBill: draft.originalBill }
      : null;

  return (
    <Stack gap={2}>
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={draft.invoiceSource}
        onChange={handleSourceChange}
      >
        {INVOICE_SOURCES.map((source) => (
          <ToggleButton key={source.value} value={source.value}>
            {source.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {draft.invoiceSource === "LINKED" ? (
        <>
          <InvoiceSearch
            key={companyId ?? "own-branch"}
            companyId={companyId}
            disabled={isBranchMissing}
            error={billLoadError || errors.invoice}
            helperText={branchHelperText}
            onSelect={onSelectInvoice}
          />
          {loadingInvoice && <CircularProgress size={20} />}
          {linkedBill && (
            <LinkedInvoicePreview
              invoice={linkedBill.invoice}
              originalBill={linkedBill.originalBill}
              originalValue={originalValue}
            />
          )}
        </>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                required
                label="เลขที่ใบกำกับภาษีเดิม"
                value={draft.manualInvoiceNo}
                onChange={handleInvoiceNoChange}
                error={Boolean(errors.manualInvoiceNo)}
                helperText={errors.manualInvoiceNo}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="วันที่ใบกำกับภาษีเดิม"
                value={draft.manualInvoiceDate}
                format="DD/MM/YYYY"
                maxDate={dayjs()}
                onChange={handleInvoiceDateChange}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                  field: { clearable: true },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                required
                label="มูลค่าสินค้าตามใบกำกับภาษีเดิม (ก่อน VAT)"
                value={draft.manualOriginalValue}
                onChange={handleOriginalValueChange}
                error={Boolean(errors.manualOriginalValue)}
                helperText={errors.manualOriginalValue}
                slotProps={{ htmlInput: { inputMode: "decimal" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                required
                label="ราคาในใบกำกับภาษีเดิม"
                value={draft.manualTaxType}
                onChange={handleTaxTypeChange}
                error={Boolean(errors.manualTaxType)}
                helperText={errors.manualTaxType}
              >
                {MANUAL_TAX_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                required
                label="การชำระเงิน"
                value={draft.settlementMethod}
                onChange={handleSettlementChange}
                error={Boolean(errors.settlementMethod)}
                helperText={errors.settlementMethod}
              >
                {NOTE_SETTLEMENT_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {NOTE_SETTLEMENT_LABEL[option]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Typography variant="body2" fontWeight={600}>
            ผู้ซื้อ
          </Typography>
          <BuyerSection
            draft={draft}
            errors={errors}
            branchUuid={branchUuid}
            isCustomerSearchBlocked={isBranchMissing}
            onChange={onChange}
          />
        </>
      )}
    </Stack>
  );
}
